import React, { useState } from 'react';
import styles from './PackageCard.module.css';
import { useTranslation } from 'react-i18next';

const PackageCard = ({ packageData, onSelect, currency = 'INR' }) => {
  const { t } = useTranslation();
  const [showCancellation, setShowCancellation] = useState(false);

  if (!packageData) return null;

  const {
    name,
    description,
    rooms,
    totalPrice,
    totalDiscount,
    roomCount,
    color,
    type
  } = packageData;

  console.log('Rendering PackageCard with data:', packageData);

  // Format price with currency
  const formatPrice = (price) => {
    if (currency === 'INR') {
      return `₹${Math.round(price).toLocaleString('en-IN')}`;
    }
    return `${currency} ${Math.round(price).toLocaleString()}`;
  };

  // Get room type summary (e.g., "3 x Business Room")
  const getRoomTypeSummary = () => {
    if (!rooms || !Array.isArray(rooms)) return '';
    
    const roomGroups = rooms.reduce((acc, room) => {
      const key = room?.roomName || 'Standard Room';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(room);
      return acc;
    }, {});

    return Object.entries(roomGroups).map(([roomType, roomList]) => ({
      type: roomType,
      count: roomList.length,
      rooms: roomList
    }));
  };

  // Get total guest count
  const getTotalGuests = () => {
    if (!rooms || !Array.isArray(rooms)) return { adults: 0, children: 0, infants: 0 };
    
    return rooms.reduce((acc, room) => ({
      adults: acc.adults + (room?.adults || 0),
      children: acc.children + (room?.children || 0),
      infants: acc.infants + (room?.infants || 0)
    }), { adults: 0, children: 0, infants: 0 });
  };

  // Get the first room image for display
  const getRepresentativeImage = () => {
    if (!rooms || !Array.isArray(rooms) || rooms.length === 0) return null;
    const firstRoomWithImage = rooms.find(room => room?.roomImages?.length > 0);
    if (firstRoomWithImage?.roomImages?.[0]?.cdnImageUrl) {
      return `${firstRoomWithImage.roomImages[0].cdnImageUrl}?tr=w-280,h-180,q-80`;
    }
    return null;
  };

  const roomTypeSummary = getRoomTypeSummary();
  const totalGuests = getTotalGuests();
  const representativeImage = getRepresentativeImage();
  const originalPrice = totalPrice + totalDiscount;
  const savingsPercentage = totalDiscount > 0 
    ? Math.round((totalDiscount / originalPrice) * 100)
    : 0;

  return (
    <div className={styles.packageCard} style={{ '--package-color': color }}>
      {/* Package Type Badge */}
      <div className={styles.packageTypeBadge} style={{ backgroundColor: color }}>
        <span className={styles.packageTypeText}>{name}</span>
        {savingsPercentage > 0 && (
          <span className={styles.savingsTag}>{savingsPercentage}% OFF</span>
        )}
      </div>

      <div className={styles.cardContent}>
        {/* Left Section - Room Information */}
        <div className={styles.leftSection}>
          {/* Room Image */}
          {representativeImage && (
            <div className={styles.imageContainer}>
              <img 
                src={representativeImage} 
                alt={roomTypeSummary[0]?.type || 'Room'} 
                className={styles.roomImage}
              />
              {rooms?.length > 1 && (
                <div className={styles.imageOverlay}>
                  <span className={styles.moreRoomsIndicator}>
                    +{rooms.length - 1} more
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Room Details */}
          <div className={styles.roomDetails}>
            <h3 className={styles.packageTitle}>{name}</h3>
            
            {/* Room Type Summary */}
            <div className={styles.roomTypeSummary}>
              {roomTypeSummary.map((group, index) => (
                <div key={index} className={styles.roomTypeItem}>
                  <span className={styles.roomCount}>{group.count} x</span>
                  <span className={styles.roomType}>{group.type}</span>
                </div>
              ))}
            </div>

            {/* Guest Distribution */}
            <div className={styles.guestInfo}>
              <div className={styles.guestItem}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                </svg>
                <span>{totalGuests.adults} Adults</span>
              </div>
              {totalGuests.children > 0 && (
                <div className={styles.guestItem}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                  </svg>
                  <span>{totalGuests.children} Children</span>
                </div>
              )}
              {totalGuests.infants > 0 && (
                <div className={styles.guestItem}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14 8.64C14 7.17 13.28 6 11.81 6S9.5 7.17 9.5 8.64c0 .83.39 1.61 1.04 2.12l-.52 3.2c-.1.55.29 1.04.85 1.04h1.88c.56 0 .95-.49.85-1.04l-.52-3.2c.65-.51 1.04-1.29 1.04-2.12z"/>
                  </svg>
                  <span>{totalGuests.infants} Infants</span>
                </div>
              )}
            </div>

            {/* Room Distribution Details */}
            <div className={styles.roomDistribution}>
              <span className={styles.distributionLabel}>Room Distribution:</span>
              <div className={styles.distributionList}>
                {rooms && rooms.length <= 4 ? (
                  // Show individual rooms if 4 or fewer
                  rooms.map((room, index) => (
                    <span key={index} className={styles.distributionItem}>
                      Rm{index + 1}: {room?.adults || 0}A
                      {room?.children > 0 && `+${room.children}C`}
                      {room?.infants > 0 && `+${room.infants}I`}
                    </span>
                  ))
                ) : (
                  // Show summary for more than 4 rooms
                  <span className={styles.distributionSummary}>
                    {rooms.length} rooms for {totalGuests.adults}A
                    {totalGuests.children > 0 && `+${totalGuests.children}C`}
                    {totalGuests.infants > 0 && `+${totalGuests.infants}I`}
                  </span>
                )}
              </div>
            </div>

            {/* Cancellation Policy */}
            {/* <div className={styles.cancellationSection}>
              <button
                className={styles.cancellationToggle}
                onClick={() => setShowCancellation(!showCancellation)}
                aria-expanded={showCancellation}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                </svg>
                <span>Cancellation Policy</span>
                <svg 
                  className={`${styles.chevron} ${showCancellation ? styles.chevronUp : ''}`} 
                  width="12" height="12" 
                  viewBox="0 0 24 24" 
                  fill="currentColor"
                >
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              {showCancellation && (
                <div className={styles.cancellationContent}>
                  <p>Free cancellation until 24 hours before check-in</p>
                </div>
              )}
            </div> */}
          </div>
        </div>

        {/* Right Section - Pricing and Action */}
        <div className={styles.rightSection}>
          <div className={styles.priceContainer}>
            {/* Combo Label */}
            <div className={styles.comboLabel}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 13c1.66 0 3-1.34 3-3S8.66 7 7 7s-3 1.34-3 3 1.34 3 3 3zm12-6h-8v7H3V5H1v15h2v-3h18v3h2v-9c0-2.21-1.79-4-4-4z"/>
              </svg>
              <span>Combo Price</span>
            </div>

            {/* Pricing */}
            <div className={styles.priceSection}>
              {totalDiscount > 0 && (
                <div className={styles.originalPrice}>
                  {formatPrice(originalPrice)}
                </div>
              )}
              <div className={styles.finalPrice}>
                {formatPrice(totalPrice)}
              </div>
              <div className={styles.priceBreakdown}>
                <span className={styles.pricePerRoom}>
                  {formatPrice(totalPrice / (rooms?.length || 1))} per room
                </span>
                <span className={styles.taxInfo}>
                  + taxes & fees
                </span>
              </div>
            </div>

            {/* Benefits */}
            {totalDiscount > 0 && (
              <div className={styles.benefits}>
                <div className={styles.benefitItem}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="green">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <span>Save {formatPrice(totalDiscount)}</span>
                </div>
              </div>
            )}

            {/* Select Button */}
            <button
              className={styles.selectButton}
              onClick={() => onSelect(packageData)}
              style={{ backgroundColor: color }}
            >
              Select Package
            </button>

            {/* Package Features */}
            <div className={styles.features}>
              {type === 'budget' && (
                <span className={styles.featureTag}>Best Value</span>
              )}
              {type === 'convenience' && (
                <span className={styles.featureTag}>Most Popular</span>
              )}
              {type === 'luxury' && (
                <span className={styles.featureTag}>Premium Choice</span>
              )}
              {type === 'smart-mix' && (
                <span className={styles.featureTag}>Smart Mix</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackageCard;